import { useState, useEffect, useRef } from "react";
import axios from "axios";

const COLOR_BAR   = "var(--azul-logo-claro)";
const COLOR_HOVER = "var(--azul-700)";
const DIAS_OPS    = [
    { key: 15, label: "15 días" },
    { key: 30, label: "30 días" },
];

// etiquetas legibles para tipos de evento
const EVENTO_LABEL = {
    ver_servicio:               "Ver servicio",
    iniciar_reserva:            "Iniciar reserva",
    abandonar_reserva:          "Abandonar reserva",
    reserva_completada:         "Reserva completada",
    agregar_favorito:           "Agregar favorito",
    iniciar_creacion_servicio:  "Iniciar creación servicio",
    paso_creacion_servicio:     "Paso creación servicio",
    poner_en_revision:          "Poner en revisión",
    abandonar_creacion_servicio:"Abandonar creación servicio",
    click_llamar_ayuda:         "Click llamar ayuda",
    click_whatsapp_ayuda:       "Click WhatsApp ayuda",
    registro_completado:        "Registro completado",
    login:                      "Login",
    suscribir_klaviyo:          "Suscribir Klaviyo",
    click_descarga_app:         "Click descarga app",
};

const EVENTO_COLORES = [
    "var(--azul-logo-claro)","var(--verde-esmeralda)","var(--violeta-mapa)","var(--ambar-600)","var(--rojo-600)",
    "var(--cian-600)","var(--verde-lima-600)","var(--purpura-600)","var(--naranja-600)","var(--verde-azulado-oscuro)",
];

function fmtFecha(iso) {
    if (!iso) return "";
    const [, m, d] = iso.split("-");
    const meses = ["ene","feb","mar","abr","may","jun","jul","ago","sep","oct","nov","dic"];
    return `${Number(d)} ${meses[Number(m) - 1]}`;
}

function fmtFechaCompleta(iso) {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    const meses = ["enero","febrero","marzo","abril","mayo","junio","julio","agosto","septiembre","octubre","noviembre","diciembre"];
    return `${Number(d)} de ${meses[Number(m) - 1]} de ${y}`;
}

// ── Gráfica de barras SVG ─────────────────────────────────────────────────────
function BarraChart({ serie, dias }) {
    const svgRef = useRef(null);
    const [tooltip, setTooltip] = useState(null);

    const W = 1000, H = 220, PAD_L = 36, PAD_R = 12, PAD_T = 16, PAD_B = 36;
    const AREA_W = W - PAD_L - PAD_R;
    const AREA_H = H - PAD_T - PAD_B;
    const maxVal    = Math.max(...serie.map(d => d.total), 1);
    const gridLines = 4;
    const barWidth  = AREA_W / serie.length;
    const barPad    = barWidth * 0.25;
    const barActual = barWidth - barPad * 2;
    const labelCada = dias <= 15 ? 3 : 5;

    function xBar(i)     { return PAD_L + i * barWidth + barPad; }
    function yBar(total) { return PAD_T + AREA_H - (total / maxVal) * AREA_H; }
    function hBar(total) { return (total / maxVal) * AREA_H; }

    const handleMouseMove = (e) => {
        const svg = svgRef.current;
        if (!svg) return;
        const rect = svg.getBoundingClientRect();
        const relX = (e.clientX - rect.left) * (W / rect.width) - PAD_L;
        const idx  = Math.floor(relX / barWidth);
        if (idx < 0 || idx >= serie.length) { setTooltip(null); return; }
        const d  = serie[idx];
        const cx = xBar(idx) + barActual / 2;
        setTooltip({ svgX: cx, fecha: d.fecha, total: d.total });
    };

    return (
        <div style={{ position: "relative" }}>
            <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`}
                style={{ width: "100%", height: "auto", display: "block", userSelect: "none" }}
                onMouseMove={handleMouseMove} onMouseLeave={() => setTooltip(null)}>
                {Array.from({ length: gridLines + 1 }, (_, i) => {
                    const y   = PAD_T + (i / gridLines) * AREA_H;
                    const val = Math.round(maxVal * (1 - i / gridLines));
                    return (
                        <g key={i}>
                            <line x1={PAD_L} y1={y} x2={W - PAD_R} y2={y} stroke="var(--color-track-bg)" strokeWidth="1" />
                            {val > 0 && <text x={PAD_L - 6} y={y + 4} textAnchor="end" fontSize="11" fill="var(--color-disabled-text)" fontFamily="system-ui,sans-serif">{val}</text>}
                        </g>
                    );
                })}
                {serie.map((d, i) => {
                    const x   = xBar(i);
                    const h   = hBar(d.total);
                    const y   = yBar(d.total);
                    const sel = tooltip?.fecha === d.fecha;
                    return (
                        <rect key={d.fecha} x={x} y={h > 0 ? y : PAD_T + AREA_H}
                            width={barActual} height={Math.max(h, 0)} rx="3" ry="3"
                            fill={sel ? COLOR_HOVER : COLOR_BAR}
                            opacity={tooltip && !sel ? 0.45 : 1}
                            style={{ transition: "opacity 0.1s, fill 0.1s" }} />
                    );
                })}
                {serie.map((d, i) => {
                    if (i % labelCada !== 0 && i !== serie.length - 1) return null;
                    return (
                        <text key={`lx-${i}`} x={xBar(i) + barActual / 2} y={H - 6}
                            textAnchor="middle" fontSize="10.5" fill="var(--color-disabled-text)" fontFamily="system-ui,sans-serif">
                            {fmtFecha(d.fecha)}
                        </text>
                    );
                })}
                {tooltip && (
                    <line x1={tooltip.svgX} y1={PAD_T} x2={tooltip.svgX} y2={PAD_T + AREA_H}
                        stroke={COLOR_BAR} strokeWidth="1" strokeDasharray="4 3" opacity="0.5" />
                )}
            </svg>
            {tooltip && (
                <div style={{
                    position: "absolute", top: 8,
                    left: `calc(${(tooltip.svgX / W) * 100}% + 8px)`,
                    transform: tooltip.svgX > W * 0.7 ? "translateX(calc(-100% - 16px))" : "none",
                    background: "var(--gris-900)", color: "var(--blanco)", borderRadius: 8,
                    padding: "6px 12px", fontSize: 12, pointerEvents: "none",
                    whiteSpace: "nowrap", zIndex: 10, boxShadow: "0 4px 12px rgba(var(--negro-rgb), 0.18)",
                }}>
                    <div style={{ fontWeight: 600, marginBottom: 2 }}>{fmtFechaCompleta(tooltip.fecha)}</div>
                    <div style={{ color: "rgba(var(--blanco-rgb), 0.75)" }}>
                        <span style={{ color: "var(--azul-claro-300)", fontWeight: 700, fontSize: 14 }}>{tooltip.total}</span>
                        {" "}usuario{tooltip.total !== 1 ? "s" : ""} registrado{tooltip.total !== 1 ? "s" : ""}
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Stat tile ─────────────────────────────────────────────────────────────────
function StatTile({ icono, label, valor, color, sub }) {
    return (
        <div className="card border-0 shadow-sm rounded-4 h-100">
            <div className="card-body p-4">
                <div className="mb-3">
                    <div className="rounded-3 d-flex align-items-center justify-content-center" style={{ width: 48, height: 48, background: `${color}18`, flexShrink: 0 }}>
                        <i className={`bi ${icono} fs-5`} style={{ color }}></i>
                    </div>
                </div>
                <p className="text-secondary fw-semibold mb-1" style={{ fontSize: "0.85rem" }}>{label}</p>
                <h3 className="fw-bold text-dark mb-1">{valor}</h3>
                {sub && <p className="text-secondary mb-0" style={{ fontSize: "0.82rem" }}>{sub}</p>}
            </div>
        </div>
    );
}

// ── Barra horizontal con porcentaje ──────────────────────────────────────────
function FilaBarra({ label, total, pct, color }) {
    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-1">
                <span style={{ fontSize: "0.84rem", color: "var(--color-text-secondary)", fontWeight: 500 }}>{label}</span>
                <div className="d-flex align-items-center gap-2">
                    <span style={{ fontSize: "0.78rem", color: "var(--color-text-secondary)" }}>{total.toLocaleString("es-CO")}</span>
                    <span style={{ fontSize: "0.82rem", fontWeight: 700, color, minWidth: 36, textAlign: "right" }}>{pct}%</span>
                </div>
            </div>
            <div style={{ background: "var(--color-track-bg)", borderRadius: 999, height: 7, overflow: "hidden" }}>
                <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 999, transition: "width 0.5s ease" }} />
            </div>
        </div>
    );
}

// ── Componente principal ──────────────────────────────────────────────────────
function ReportesPorUsuarios() {
    const [dias,     setDias]     = useState(30);
    const [data,     setData]     = useState(null);
    const [cargando, setCargando] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                setCargando(true);
                const res = await axios.post("/reportes-usuarios", { params: { dias } });
                if (res.data.exito) setData(res.data);
            } catch { setData(null); }
            finally  { setCargando(false); }
        })();
    }, [dias]);

    const serie       = data?.serie       || [];
    const stats       = data;
    const completitud = data?.completitud || null;
    const modos       = data?.modos       || null;
    const verific     = data?.verificacion|| null;
    const topCiudades = data?.topCiudades || [];
    const eventosWeb  = data?.eventosWeb  || [];
    const eventosApp  = data?.eventosApp  || [];

    const verificOrden = [
        { key: "aprobado",  label: "Aprobado",  color: "var(--verde-esmeralda)" },
        { key: "pendiente", label: "Pendiente", color: "var(--ambar-600)" },
        { key: "rechazado", label: "Rechazado", color: "var(--rojo-600)" },
        { key: "inicio",    label: "Sin iniciar",color: "var(--color-text-secondary)" },
    ];

    return (
        <div className="container-fluid">

            {/* Encabezado */}
            <div className="d-flex justify-content-between flex-wrap align-items-center gap-3 pt-3 pb-2 mb-4 border-bottom">
                <div>
                    <h1 className="h2 text-dark mb-1">Reportes · Usuarios</h1>
                    <p className="text-secondary mb-0 small">Analítica de usuarios registrados en la plataforma.</p>
                </div>
                <div className="d-flex gap-1">
                    {DIAS_OPS.map(op => (
                        <button key={op.key} type="button"
                            className={`btn btn-sm rounded-pill px-3 ${dias === op.key ? "btn-dark" : "btn-outline-secondary"}`}
                            style={{ fontSize: "0.78rem" }}
                            onClick={() => setDias(op.key)}>
                            {op.label}
                        </button>
                    ))}
                </div>
            </div>

            {cargando ? (
                <div className="text-center py-5">
                    <span className="spinner-border text-primary" role="status" />
                </div>
            ) : (
                <>
                    {/* ── Stats tiles ── */}
                    {stats && (
                        <div className="row g-3 mb-4">
                            <div className="col-6 col-md-3">
                                <StatTile icono="bi-people-fill" label="Total usuarios" color="var(--azul-logo-claro)"
                                    valor={(stats.totalUsuarios || 0).toLocaleString("es-CO")} />
                            </div>
                            <div className="col-6 col-md-3">
                                <StatTile icono="bi-person-plus-fill" label={`Nuevos (${dias}d)`} color="var(--verde-esmeralda)"
                                    valor={(stats.totalPeriodo || 0).toLocaleString("es-CO")} />
                            </div>
                            <div className="col-6 col-md-3">
                                <StatTile icono="bi-graph-up" label="Promedio / día" color="var(--violeta-mapa)"
                                    valor={stats.promedioDia ?? 0} />
                            </div>
                            <div className="col-6 col-md-3">
                                <StatTile
                                    icono="bi-trophy-fill"
                                    label={`Pico${stats.pico?.fecha ? ` · ${fmtFecha(stats.pico.fecha)}` : ""}`}
                                    color="var(--ambar-600)"
                                    valor={stats.pico?.total ?? 0} />
                            </div>
                        </div>
                    )}

                    {/* ── Gráfica registros por día ── */}
                    <div className="card border-0 shadow-sm rounded-4 mb-4">
                        <div className="card-body p-4">
                            <div className="d-flex align-items-center gap-2 mb-4">
                                <div style={{ width: 12, height: 12, borderRadius: 3, background: COLOR_BAR }}></div>
                                <span className="fw-semibold text-dark" style={{ fontSize: "0.92rem" }}>
                                    Usuarios registrados por día — últimos {dias} días
                                </span>
                            </div>
                            {serie.length > 0
                                ? <BarraChart serie={serie} dias={dias} />
                                : <div className="text-center py-5 text-secondary"><i className="bi bi-bar-chart fs-2 d-block mb-2"></i>Sin datos.</div>
                            }
                        </div>
                    </div>

                    {/* ── Completitud + Modo ── */}
                    {(completitud || modos) && (
                        <div className="row g-3 mb-4">

                            {/* Completitud de perfiles */}
                            {completitud && (
                                <div className="col-12 col-lg-7">
                                    <div className="card border-0 shadow-sm rounded-4 h-100">
                                        <div className="card-body p-4">
                                            <h6 className="fw-bold text-dark mb-1" style={{ fontSize: "0.92rem" }}>
                                                <i className="bi bi-person-check-fill me-2" style={{ color: "var(--verde-esmeralda)" }}></i>
                                                Completitud de perfiles
                                            </h6>
                                            <p className="text-secondary mb-3" style={{ fontSize: "0.78rem" }}>
                                                Sobre {(data?.baseUsuarios || 0).toLocaleString("es-CO")} usuarios registrados
                                            </p>
                                            <div className="d-flex flex-column gap-3">
                                                <FilaBarra label="Perfil personal completo"
                                                    total={completitud.personal.total}
                                                    pct={completitud.personal.pct}
                                                    color="var(--azul-logo-claro)" />
                                                <FilaBarra label="Perfil profesional completo"
                                                    total={completitud.profesional.total}
                                                    pct={completitud.profesional.pct}
                                                    color="var(--violeta-mapa)" />
                                                <FilaBarra label="Info bancaria completa"
                                                    total={completitud.bancaria.total}
                                                    pct={completitud.bancaria.pct}
                                                    color="var(--verde-esmeralda)" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Modo comprador / vendedor */}
                            {modos && (
                                <div className="col-12 col-lg-5">
                                    <div className="card border-0 shadow-sm rounded-4 h-100">
                                        <div className="card-body p-4">
                                            <h6 className="fw-bold text-dark mb-3" style={{ fontSize: "0.92rem" }}>
                                                <i className="bi bi-people me-2" style={{ color: "var(--azul-logo-claro)" }}></i>
                                                Compradores vs Vendedores
                                            </h6>
                                            {[
                                                { key: "Comprador", color: "var(--azul-logo-claro)", icono: "bi-bag" },
                                                { key: "Vendedor",  color: "var(--violeta-mapa)", icono: "bi-briefcase" },
                                            ].map(({ key, color, icono }) => {
                                                const total = modos[key] || 0;
                                                const base  = (modos.Comprador || 0) + (modos.Vendedor || 0);
                                                const pct   = base ? Math.round((total / base) * 100) : 0;
                                                return (
                                                    <div key={key} className="mb-3">
                                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                                            <div className="d-flex align-items-center gap-2">
                                                                <div style={{ width: 32, height: 32, borderRadius: 8, background: `${color}18`, display:"flex", alignItems:"center", justifyContent:"center" }}>
                                                                    <i className={`bi ${icono}`} style={{ color, fontSize: 14 }}></i>
                                                                </div>
                                                                <span style={{ fontSize: "0.88rem", fontWeight: 600, color: "var(--color-text-primary)" }}>{key}s</span>
                                                            </div>
                                                            <div className="text-end">
                                                                <div style={{ fontSize: "1.2rem", fontWeight: 700, color, lineHeight: 1 }}>{total.toLocaleString("es-CO")}</div>
                                                                <div style={{ fontSize: "0.72rem", color: "var(--color-text-secondary)" }}>{pct}%</div>
                                                            </div>
                                                        </div>
                                                        <div style={{ background: "var(--color-track-bg)", borderRadius: 999, height: 6, overflow: "hidden" }}>
                                                            <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 999, transition: "width 0.5s ease" }} />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── Verificación de identidad ── */}
                    {verific && (
                        <div className="card border-0 shadow-sm rounded-4 mb-4">
                            <div className="card-body p-4">
                                <h6 className="fw-bold text-dark mb-3" style={{ fontSize: "0.92rem" }}>
                                    <i className="bi bi-shield-check me-2" style={{ color: "var(--ambar-600)" }}></i>
                                    Verificación de identidad
                                </h6>
                                <div className="row g-3">
                                    {verificOrden.map(({ key, label, color }) => {
                                        const total = verific[key] || 0;
                                        const base  = Object.values(verific).reduce((s, v) => s + v, 0);
                                        const pct   = base ? Math.round((total / base) * 100) : 0;
                                        return (
                                            <div className="col-6 col-md-3" key={key}>
                                                <div className="rounded-3 p-3" style={{ background: `${color}0d`, border: `1px solid ${color}20` }}>
                                                    <div style={{ fontSize: "0.75rem", color: "var(--color-text-secondary)", marginBottom: 4 }}>{label}</div>
                                                    <div style={{ fontSize: "1.5rem", fontWeight: 700, color, lineHeight: 1 }}>{total.toLocaleString("es-CO")}</div>
                                                    <div style={{ fontSize: "0.72rem", color: "var(--gris-400)", marginTop: 2 }}>{pct}% del total</div>
                                                    <div style={{ background: "var(--color-track-bg)", borderRadius: 999, height: 4, marginTop: 8, overflow: "hidden" }}>
                                                        <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 999 }} />
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Top ciudades ── */}
                    {topCiudades.length > 0 && (() => {
                        const maxCiudad = topCiudades[0]?.total || 1;
                        const COLORES   = ["var(--azul-logo-claro)","var(--verde-esmeralda)","var(--violeta-mapa)","var(--ambar-600)","var(--rojo-600)","var(--cian-600)","var(--verde-lima-600)","var(--purpura-600)","var(--naranja-600)","var(--verde-azulado-oscuro)"];
                        return (
                            <div className="card border-0 shadow-sm rounded-4 mb-4">
                                <div className="card-body p-4">
                                    <h6 className="fw-bold text-dark mb-1" style={{ fontSize: "0.92rem" }}>
                                        <i className="bi bi-geo-alt-fill me-2" style={{ color: "var(--azul-logo-claro)" }}></i>
                                        Principales ciudades
                                    </h6>
                                    <p className="text-secondary mb-4" style={{ fontSize: "0.78rem" }}>
                                        Usuarios registrados por ciudad
                                    </p>
                                    <div className="row g-3">
                                        {topCiudades.map(({ ciudad, total }, i) => {
                                            const pct   = Math.round((total / maxCiudad) * 100);
                                            const color = COLORES[i % COLORES.length];
                                            return (
                                                <div key={ciudad} className="col-12 col-md-6">
                                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }}></div>
                                                            <span style={{ fontSize: "0.84rem", color: "var(--color-text-secondary)" }}>{ciudad}</span>
                                                        </div>
                                                        <span style={{ fontSize: "0.84rem", fontWeight: 700, color }}>{total.toLocaleString("es-CO")}</span>
                                                    </div>
                                                    <div style={{ background: "var(--color-track-bg)", borderRadius: 999, height: 6, overflow: "hidden" }}>
                                                        <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 999, transition: "width 0.5s ease" }} />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        );
                    })()}

                    {/* ── Actividad en la plataforma — Web y App ── */}
                    {(() => {
                        const CONFIG_EV = {
                            registro_completado: { label: "Registro completado", color: "var(--azul-logo-claro)", icono: "bi-person-plus-fill" },
                            suscribir_klaviyo:   { label: "Suscribir Klaviyo",   color: "var(--violeta-mapa)", icono: "bi-envelope-fill"    },
                            click_llamar_ayuda:  { label: "Click llamar ayuda",  color: "var(--verde-esmeralda)", icono: "bi-telephone-fill"   },
                            agregar_favorito:    { label: "Agregar favorito",    color: "var(--ambar-600)", icono: "bi-heart-fill"       },
                        };

                        function PanelEventos({ titulo, icono, eventos }) {
                            const max = Math.max(...eventos.map(e => e.total), 1);
                            return (
                                <div className="card border-0 shadow-sm rounded-4 h-100">
                                    <div className="card-body p-4">
                                        <h6 className="fw-bold text-dark mb-3" style={{ fontSize: "0.88rem" }}>
                                            <i className={`bi ${icono} me-2`} style={{ color: "var(--ambar-600)" }}></i>
                                            {titulo}
                                        </h6>
                                        <div className="d-flex flex-column gap-3">
                                            {eventos.map(({ tipo, total }) => {
                                                const cfg = CONFIG_EV[tipo] || { label: tipo, color: "var(--color-text-secondary)", icono: "bi-dot" };
                                                const pct = Math.round((total / max) * 100);
                                                return (
                                                    <div key={tipo}>
                                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                                            <div className="d-flex align-items-center gap-2">
                                                                <i className={`bi ${cfg.icono}`} style={{ color: cfg.color, fontSize: 13 }}></i>
                                                                <span style={{ fontSize: "0.84rem", color: "var(--color-text-secondary)" }}>{cfg.label}</span>
                                                            </div>
                                                            <span style={{ fontSize: "0.84rem", fontWeight: 700, color: cfg.color }}>{total.toLocaleString("es-CO")}</span>
                                                        </div>
                                                        <div style={{ background: "var(--color-track-bg)", borderRadius: 999, height: 6, overflow: "hidden" }}>
                                                            <div style={{ width: `${pct}%`, height: "100%", background: cfg.color, borderRadius: 999, transition: "width 0.5s ease" }} />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            );
                        }

                        return (
                            <div className="mb-4">
                                <div className="mb-3">
                                    <h6 className="fw-bold text-dark mb-0" style={{ fontSize: "0.92rem" }}>
                                        <i className="bi bi-lightning-charge-fill me-2" style={{ color: "var(--ambar-600)" }}></i>
                                        Actividad en la plataforma
                                    </h6>
                                    <p className="text-secondary mb-0 mt-1" style={{ fontSize: "0.78rem" }}>
                                        Últimos 30 días — registro, Klaviyo, llamadas y favoritos.
                                    </p>
                                </div>
                                <div className="row g-3">
                                    <div className="col-12 col-md-6">
                                        <PanelEventos titulo="Web" icono="bi-globe2" eventos={eventosWeb} />
                                    </div>
                                    <div className="col-12 col-md-6">
                                        <PanelEventos titulo="App" icono="bi-phone-fill" eventos={eventosApp} />
                                    </div>
                                </div>
                            </div>
                        );
                    })()}
                </>
            )}
        </div>
    );
}

export default ReportesPorUsuarios;
